import type {
  RaceHudState,
  RacePhase,
  RaceResult,
  VehicleTelemetry,
} from '../core/types';
import type {
  CheckpointAttempt,
  FinishAttempt,
  RaceCheckpoint,
  RaceManagerOptions,
  RaceStateSnapshot,
  TrackTransform,
} from './contracts';
import { clamp, copyVector, finiteOr } from './math';

const DEFAULT_COUNTDOWN_SECONDS = 3;
const DEFAULT_GO_DISPLAY_SECONDS = 0.75;

function cloneTransform(transform: TrackTransform): TrackTransform {
  return {
    ...transform,
    position: copyVector(transform.position),
  };
}

function validBestTime(value: number | null | undefined): number | null {
  return value !== null && value !== undefined && Number.isFinite(value) && value >= 0
    ? value
    : null;
}

/** Pure, pause-safe state machine for a single time-trial attempt. */
export class RaceManager {
  readonly trackId: string;
  readonly carId: string;

  private readonly startTransform: TrackTransform;
  private readonly checkpoints: readonly RaceCheckpoint[];
  private readonly checkpointIndexById = new Map<string, number>();
  private readonly countdownSeconds: number;
  private readonly goDisplaySeconds: number;
  private phase: RacePhase = 'loading';
  private elapsedSeconds = 0;
  private countdownElapsedSeconds = 0;
  private goDisplayRemainingSeconds = 0;
  private completedCheckpoints = 0;
  private paused = false;
  private missingCheckpointWarning = false;
  private notice: string | null = null;
  private bestTimeMs: number | null;
  private result: RaceResult | null = null;

  constructor(options: RaceManagerOptions) {
    this.trackId = options.trackId;
    this.carId = options.carId;
    this.startTransform = cloneTransform(options.startTransform);
    this.checkpoints = options.checkpoints.map((checkpoint) => ({
      ...checkpoint,
      resetTransform: cloneTransform(checkpoint.resetTransform),
    }));
    this.countdownSeconds = Math.max(
      0,
      finiteOr(options.countdownSeconds, DEFAULT_COUNTDOWN_SECONDS),
    );
    this.goDisplaySeconds = Math.max(
      0,
      finiteOr(options.goDisplaySeconds, DEFAULT_GO_DISPLAY_SECONDS),
    );
    this.bestTimeMs = validBestTime(options.bestTimeMs);

    this.checkpoints.forEach((checkpoint, index) => {
      if (this.checkpointIndexById.has(checkpoint.id)) {
        throw new Error(`Duplicate checkpoint id: ${checkpoint.id}`);
      }
      this.checkpointIndexById.set(checkpoint.id, index);
    });

    if (options.autoStart ?? true) this.startCountdown();
  }

  startCountdown(): void {
    this.phase = this.countdownSeconds > 0 ? 'countdown' : 'racing';
    this.elapsedSeconds = 0;
    this.countdownElapsedSeconds = 0;
    this.goDisplayRemainingSeconds = this.countdownSeconds > 0 ? 0 : this.goDisplaySeconds;
    this.completedCheckpoints = 0;
    this.paused = false;
    this.missingCheckpointWarning = false;
    this.notice = null;
    this.result = null;
  }

  /** Places the manager back into a non-running track-loaded state. */
  prepare(): void {
    this.phase = 'loading';
    this.elapsedSeconds = 0;
    this.countdownElapsedSeconds = 0;
    this.goDisplayRemainingSeconds = 0;
    this.completedCheckpoints = 0;
    this.paused = false;
    this.missingCheckpointWarning = false;
    this.notice = null;
    this.result = null;
  }

  update(deltaSeconds: number, pausedOverride?: boolean): RaceStateSnapshot {
    if (!Number.isFinite(deltaSeconds) || deltaSeconds <= 0) return this.getSnapshot();
    if (pausedOverride ?? this.paused) return this.getSnapshot();
    if (this.phase === 'loading' || this.phase === 'finished') return this.getSnapshot();

    let remaining = deltaSeconds;
    if (this.phase === 'countdown') {
      const untilGo = Math.max(0, this.countdownSeconds - this.countdownElapsedSeconds);
      const countdownDelta = Math.min(remaining, untilGo);
      this.countdownElapsedSeconds += countdownDelta;
      remaining -= countdownDelta;

      if (this.countdownElapsedSeconds + 1e-10 >= this.countdownSeconds) {
        this.countdownElapsedSeconds = this.countdownSeconds;
        this.phase = 'racing';
        this.goDisplayRemainingSeconds = this.goDisplaySeconds;
      }
    }

    if (this.phase === 'racing' && remaining > 0) {
      this.elapsedSeconds += remaining;
      this.goDisplayRemainingSeconds = Math.max(
        0,
        this.goDisplayRemainingSeconds - remaining,
      );
    }
    return this.getSnapshot();
  }

  setPaused(paused: boolean): void {
    this.paused = this.phase === 'finished' ? false : paused;
  }

  isPaused(): boolean {
    return this.paused;
  }

  canDrive(): boolean {
    return this.phase === 'racing' && !this.paused;
  }

  passCheckpoint(checkpoint: string | number | Pick<RaceCheckpoint, 'id'>): CheckpointAttempt {
    if (this.phase !== 'racing') {
      return this.checkpointAttempt(false, 'race-not-active');
    }

    const index = this.resolveCheckpointIndex(checkpoint);
    if (index === null) {
      this.notice = 'Unknown checkpoint';
      return this.checkpointAttempt(false, 'unknown-checkpoint');
    }
    if (index < this.completedCheckpoints) {
      return this.checkpointAttempt(false, 'already-passed');
    }
    if (index !== this.completedCheckpoints) {
      const expected = this.checkpoints[this.completedCheckpoints];
      this.notice = expected
        ? `Checkpoint ${this.completedCheckpoints + 1} must be crossed next`
        : null;
      return this.checkpointAttempt(false, 'out-of-order');
    }

    this.completedCheckpoints += 1;
    this.missingCheckpointWarning = false;
    this.notice = `Checkpoint ${this.completedCheckpoints} / ${this.checkpoints.length}`;
    return this.checkpointAttempt(true, 'accepted');
  }

  /** Familiar alias for trigger volumes. */
  crossCheckpoint(checkpoint: string | number | Pick<RaceCheckpoint, 'id'>): CheckpointAttempt {
    return this.passCheckpoint(checkpoint);
  }

  tryFinish(): FinishAttempt {
    if (this.phase !== 'racing') {
      return {
        accepted: false,
        missingCheckpoints: Math.max(0, this.checkpoints.length - this.completedCheckpoints),
        result: null,
      };
    }

    const missingCheckpoints = this.checkpoints.length - this.completedCheckpoints;
    if (missingCheckpoints > 0) {
      this.missingCheckpointWarning = true;
      this.notice = `${missingCheckpoints} checkpoint${missingCheckpoints === 1 ? '' : 's'} missing`;
      return { accepted: false, missingCheckpoints, result: null };
    }

    const finalTimeMs = Math.max(0, Math.round(this.elapsedSeconds * 1000));
    const previousBestMs = this.bestTimeMs;
    const isNewBest = previousBestMs === null || finalTimeMs < previousBestMs;
    const differenceMs = previousBestMs === null ? null : finalTimeMs - previousBestMs;
    this.result = {
      trackId: this.trackId,
      carId: this.carId,
      finalTimeMs,
      previousBestMs,
      isNewBest,
      differenceMs,
    };
    if (isNewBest) this.bestTimeMs = finalTimeMs;
    this.phase = 'finished';
    this.paused = false;
    this.goDisplayRemainingSeconds = 0;
    this.missingCheckpointWarning = false;
    this.notice = isNewBest ? 'NEW PERSONAL BEST' : 'Race complete';
    return { accepted: true, missingCheckpoints: 0, result: this.getResult() };
  }

  finish(): FinishAttempt {
    return this.tryFinish();
  }

  /**
   * Starts a completely fresh attempt and returns the transform the vehicle
   * should use immediately. No scene or track reconstruction is required.
   */
  restart(bestTimeMs: number | null = this.bestTimeMs): TrackTransform {
    this.bestTimeMs = validBestTime(bestTimeMs);
    this.startCountdown();
    return this.getStartTransform();
  }

  instantRestart(bestTimeMs: number | null = this.bestTimeMs): TrackTransform {
    return this.restart(bestTimeMs);
  }

  /** Returns the latest safe transform without altering time or progress. */
  resetToCheckpoint(): TrackTransform {
    const checkpoint =
      this.completedCheckpoints > 0 ? this.checkpoints[this.completedCheckpoints - 1] : null;
    this.notice = checkpoint
      ? `Reset to checkpoint ${this.completedCheckpoints}`
      : 'Reset to start';
    return checkpoint
      ? cloneTransform(checkpoint.resetTransform)
      : this.getStartTransform();
  }

  getStartTransform(): TrackTransform {
    return cloneTransform(this.startTransform);
  }

  getLatestResetTransform(): TrackTransform {
    const checkpoint =
      this.completedCheckpoints > 0 ? this.checkpoints[this.completedCheckpoints - 1] : null;
    return checkpoint
      ? cloneTransform(checkpoint.resetTransform)
      : this.getStartTransform();
  }

  setBestTime(bestTimeMs: number | null): void {
    this.bestTimeMs = validBestTime(bestTimeMs);
  }

  getBestTime(): number | null {
    return this.bestTimeMs;
  }

  getResult(): RaceResult | null {
    return this.result ? { ...this.result } : null;
  }

  getSnapshot(): RaceStateSnapshot {
    return {
      phase: this.phase,
      timeMs: this.timeMs(),
      countdownText: this.countdownText(),
      completedCheckpoints: this.completedCheckpoints,
      checkpointCount: this.checkpoints.length,
      nextCheckpointId: this.checkpoints[this.completedCheckpoints]?.id ?? null,
      missingCheckpointWarning: this.missingCheckpointWarning,
      notice: this.notice,
      paused: this.paused,
      canDrive: this.canDrive(),
    };
  }

  getHudState(telemetry: VehicleTelemetry): RaceHudState {
    const snapshot = this.getSnapshot();
    return {
      phase: snapshot.phase,
      timeMs: snapshot.timeMs,
      countdownText: snapshot.countdownText,
      checkpointIndex: snapshot.completedCheckpoints,
      checkpointCount: snapshot.checkpointCount,
      missingCheckpointWarning: snapshot.missingCheckpointWarning,
      bestTimeMs: this.bestTimeMs,
      telemetry: {
        ...telemetry,
        position: copyVector(telemetry.position),
      },
      notice: snapshot.notice,
    };
  }

  clearNotice(): void {
    this.notice = null;
    this.missingCheckpointWarning = false;
  }

  private resolveCheckpointIndex(
    checkpoint: string | number | Pick<RaceCheckpoint, 'id'>,
  ): number | null {
    if (typeof checkpoint === 'number') {
      if (!Number.isInteger(checkpoint) || checkpoint < 0 || checkpoint >= this.checkpoints.length) {
        return null;
      }
      return checkpoint;
    }
    const id = typeof checkpoint === 'string' ? checkpoint : checkpoint.id;
    return this.checkpointIndexById.get(id) ?? null;
  }

  private checkpointAttempt(
    accepted: boolean,
    reason: CheckpointAttempt['reason'],
  ): CheckpointAttempt {
    return {
      accepted,
      reason,
      completedCheckpoints: this.completedCheckpoints,
      expectedCheckpointId: this.checkpoints[this.completedCheckpoints]?.id ?? null,
    };
  }

  private countdownText(): string | null {
    if (this.phase === 'countdown') {
      const remaining = this.countdownSeconds - this.countdownElapsedSeconds;
      return String(clamp(Math.ceil(remaining), 1, 3));
    }
    if (this.phase === 'racing' && this.goDisplayRemainingSeconds > 0) return 'GO';
    return null;
  }

  private timeMs(): number {
    return Math.max(0, this.elapsedSeconds * 1000);
  }
}

